import flow from 'lodash/flow';
import get from 'lodash/get';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';

import dom from '../../wrapper/DomWrapper';
import {
  actualProductPerRow, getHeaderHeight, getLimit, getModifier, toDom,
} from './utils';
import { DEVICES } from '../../../device';
import { getDeviceType } from '../../helpers/browser';
import { subscribeToDeviceChanging } from '../../observer/deviceObserver';
import getStateValue from '../../helpers/getStateValue';
import ieChangeCssVar from '../../helpers/ieChangeCssVar';

export const DEVICE_SCROLL_OFFSET = {
  [DEVICES.PHONE]: 20,
  [DEVICES.TABLET]: 30,
  [DEVICES.LAPTOP]: 40,
  [DEVICES.DESKTOP]: 60,
  [DEVICES.QUAD]: 90,
};

class LayoutController {
  components = {};

  lastMeta = {};

  lastData = {};

  lastEvent = '';

  constructor(
    container,
    settings,
    itemRenderer,
    placeholderRenderer,
    spinnerRenderer,
    retryRenderer,
    pagination,
  ) {
    this.container = container;
    this.settings = settings;
    this.itemRenderer = itemRenderer;
    this.placeholderRenderer = placeholderRenderer;
    this.spinnerRenderer = spinnerRenderer;
    this.retryRenderer = retryRenderer;
    this.pagination = pagination;

    const { layoutSettings } = settings;
    this.realGrid = get(layoutSettings, 'realGrid');
  }

  saveMeta = (meta) => {
    this.lastMeta = meta;
    return meta;
  };

  setLoading = (isLoading, event) => {
    this.isLoading = isLoading;
    const { noLoadingState, spinnerPerItem, spinnerOnce } = this.settings;

    if (noLoadingState) return;

    const elItemsContainer = this.getItemsContainer();

    if (!elItemsContainer) return;

    if (!spinnerPerItem) {
      if (!isLoading) {
        elItemsContainer.style['min-height'] = '';

        return;
      }
      if (spinnerOnce && !['init', 'retry'].includes(event)) return;

      const spinner = flow(
        this.spinnerRenderer.render,
        toDom,
      )();

      if (event !== 'change') {
        dom.updateStyle(
          elItemsContainer,
          { minHeight: `${Math.round(dom.window.innerHeight / 2)}px` },
        );
      }

      elItemsContainer.innerHTML = '';
      elItemsContainer.appendChild(spinner);
    }
  };

  init = async () => {
    const { type } = this.settings;
    const preInit = getModifier(type, 'preInit');

    if (preInit) {
      const components = await preInit();
      this.components = { ...this.components, ...components };
    }

    this.pagination.on(['init', 'change'], async (data, event) => {
      this.lastData = data;
      this.lastEvent = event;
      await this.runAction(data, event);
    });

    this.pagination.init();
    subscribeToDeviceChanging(this, this.calculateGrid);
  };

  runAction = async (data = this.lastData, event = this.lastEvent) => {
    const { emptyListClassName, itemSize, layoutSettings } = this.settings;
    this.setLoading(true, event);

    if (this.fetchError) {
      const realLimit = getLimit(
        itemSize,
        layoutSettings[layoutSettings.layoutType],
        getDeviceType(),
      );

      if (realLimit !== data.limit) {
        // eslint-disable-next-line no-param-reassign
        data.limit = realLimit;
        // eslint-disable-next-line no-param-reassign
        data.offset = 0;
      }
    }

    dom.removeClass(this.container, emptyListClassName);
    dom.removeClass(this.container, '_show');
    this.response = await this.fetchItems(data);
    if (!this.response) return;

    flow(
      this.saveMeta,
      this.drawItems,
      (dataOb) => {
        this.pagination.setState('isLoading', false);
        this.resizeItems(getDeviceType());
        return dataOb;
      },
      this.drawPager,
      () => {
        this.setLoading(false, event);
        if (event === 'change') this.scrollTop();
      },
    )(this.response);
  };

  redraw = (limit) => {
    const { offset } = this.lastMeta;
    const length = get(this, 'response.items.length');
    const device = getDeviceType();

    flow(
      this.drawItems,
      this.drawPager,
      () => this.resizeItems(device),
    )(this.response);

    if (length >= limit && !offset) {
      return;
    }

    this.pagination.setPage(0);
  };

  calculateGrid = (device) => {
    const {
      layoutSettings: {
        layoutType,
      },
      layoutSettings,
      itemSize,
    } = this.settings;

    if (this.realGrid) {
      const limit = getLimit(itemSize, layoutSettings[layoutType], getDeviceType());
      if (limit === this.pagination.state.limit) return;

      this
        .pagination
        .setState('limit', limit);
      if (this.fetchError) {
        this.drawRetry();
      } else {
        this.redraw(limit);
      }

      return;
    }

    this.resizeItems(device);
  };

  resizeItems = (device) => {
    const {
      layoutSettings: {
        grid: { items_per_row: items } = {},
        layoutType,
      },
      itemSize,
      itemClassName,
    } = this.settings;

    if (layoutType !== 'grid' || !items) return;

    const elItemsContainer = this.getItemsContainer();

    if (!elItemsContainer) return;

    const newItemsPerRow = actualProductPerRow(device, itemSize, { items_per_row: items });

    [...elItemsContainer.classList].forEach((className) => {
      const matches = className.match(/^(.*)-per-row_(\d+)$/);
      if (!matches) return;

      if (parseInt(matches[2], 10) === newItemsPerRow) return;

      dom.removeClass(elItemsContainer, className);
      dom.addClass(elItemsContainer, `${matches[1]}-per-row_${newItemsPerRow}`);
    });

    dom.getCollection(`.${itemClassName}`, elItemsContainer).forEach((node) => {
      dom.updateStyle(node, { width: `${100 / newItemsPerRow}%` });
    });

    ieChangeCssVar();
  };

  getItems = ({ limit = 10, offset = 0 }) => {
    const { source, requestParams = {}, type } = this.settings;

    const params = isObject(requestParams)
      ? { ...requestParams, limit, offset }
      : { limit, offset };

    if (isString(source)) {
      const paramsString = Object
        .keys(params)
        .map((key) => `${key}=${encodeURIComponent(params[key])}`).join('&');

      return dom.window
        .fetch(`${source}${source.includes('?') ? '&' : '?'}${paramsString}`)
        .then((response) => response.json());
    }

    if (isObject(source) && type === 'blog') {
      const blogTag = getStateValue('blogTag', null);
      let url = source.blog;

      if (blogTag) url = `${source.tag}/${blogTag.slug}/post`;

      const paramsString = Object
        .keys(params)
        .map((key) => `${key}=${encodeURIComponent(params[key])}`).join('&');

      return dom.window
        .fetch(`${url}${url.includes('?') ? '&' : '?'}${paramsString}`)
        .then((response) => response.json());
    }

    const getter = get(this, ['components', ...source]);
    if (!getter) {
      throw new Error('Wrong configuration');
    }

    return getter(params);
  };

  fetchItems = (meta) => {
    this.fetchError = false;

    return this.getItems(meta)
      .then((response) => {
        this.response = response;
        return this.response;
      })
      .catch(this.onFetchError);
  };

  onFetchError = (e) => {
    this.setLoading(false);
    const {
      withRetry,
    } = this.settings;
    if (!withRetry) return;

    this.fetchError = true;

    this.drawRetry();
    // eslint-disable-next-line no-console
    console.error(e);
  };

  drawRetry = () => {
    const {
      itemClassName,
      emptyListClassName,
      itemSize,
      layoutSettings,
    } = this.settings;

    const elItemsContainer = this.getItemsContainer();
    if (!elItemsContainer) return;

    const device = getDeviceType();
    const limit = getLimit(itemSize, layoutSettings[layoutSettings.layoutType], device);
    const retryData = {
      items: Array(Math.min(limit, 5)).fill(1),
      itemClassName,
    };

    const retry = flow(
      this.retryRenderer.render,
      toDom,
    )(retryData);
    dom.removeElement(this.pagination);

    dom.addHtml(elItemsContainer, retry.innerHTML);
    dom.on(dom.getElement('._try-btn'), 'click', async () => {
      dom.addHtml(elItemsContainer, '');
      this.runAction(this.lastData, 'retry');
    });
    this.resizeItems(device);

    dom.addClass(this.container, emptyListClassName);
    dom.addClass(this.container, '_show');
  };

  drawPager = (data) => {
    if (!this.fetchError) {
      this.pagination.render(data);
    }

    return data;
  };

  getItemsContainer = () => {
    const { itemsContainerClassName } = this.settings;
    return dom.getElement(`.${itemsContainerClassName}`);
  };

  drawItems = (data) => {
    const { items, limit: defaultLimit } = data;
    const { type, paginationType, emptyListClassName } = this.settings;
    const elItemsContainer = this.getItemsContainer();

    if (!elItemsContainer || !items) return;

    if (paginationType !== 'infinite') dom.addHtml(elItemsContainer, '');
    if (!items.length && !dom.hasClass(this.container, emptyListClassName)) {
      dom.addClass(this.container, emptyListClassName);
    }

    const limit = this.realGrid ? this.pagination.state.limit : defaultLimit;
    items.slice(0, limit).forEach((item, index) => {
      const postRender = getModifier(type, 'postRender');
      const render = flow(
        (dataOb) => getModifier(type, 'preRender').call(this, dataOb, index, elItemsContainer),
        this.itemRenderer.render,
        toDom,
        (element) => postRender.call(this, element, item, index),
        (element) => elItemsContainer.appendChild(element),
      );

      render(item);
    });
    const onDidMount = getModifier(type, 'didMount');
    onDidMount(elItemsContainer);

    // eslint-disable-next-line consistent-return
    return data;
  };

  scrollTop = () => {
    if (this.settings.paginationType === 'infinite') return;

    const scrollTop = this.container.getBoundingClientRect().top
      - DEVICE_SCROLL_OFFSET[getDeviceType()]
      - getHeaderHeight();
    const doc = dom.document.documentElement;
    const top = (dom.window.pageYOffset || doc.scrollTop) - (doc.clientTop || 0);
    if (top >= scrollTop) dom.window.scrollTo(0, scrollTop);
  };
}

export default LayoutController;
